/**
 * Observables
 *
 * The perception half of the reaction engine: everything a defender can *see* about a key,
 * derived once from that key's observation feed.
 *
 * Read policies used to each re-derive velocity, heading, the vertical stem, peak speed and
 * break detection inline — six near-identical copies that had already drifted in naming. This
 * module computes them once and gives every policy the same vocabulary.
 *
 * Two properties matter:
 *
 * 1. **The stream is param-free.** Nothing here depends on a policy's thresholds, so the same
 *    stream can be built once per receiver and shared by every defender keying him. That is
 *    what makes the derived-read model (see `.kiro/steering/reaction-engine.md`) cheap: a
 *    corner and a safety both reading #2 compute his motion once, then apply their own
 *    thresholds via the predicates below.
 *
 * 2. **Latency is an index shift, not a recomputation.** The stream is built from the true
 *    feed; a defender reads it through {@link perceive}, which hands back the entry from
 *    `reactionLatencyMs` ago. Delaying a defender costs nothing and — importantly — cannot
 *    change what he concludes, only when. The delayed stream is the same evidence in the same
 *    order.
 *
 * Pure module: no React, no side effects, no randomness. Safe to unit test.
 */

import { Position, Observation, KinematicsParams } from '../types';
import { perceivedIndex } from './kinematics';

/** Smallest speed (units/sec) treated as real movement (filters out the stationary snap frame). */
export const MOVEMENT_EPS = 2;

/**
 * Field units per yard. The whole coordinate system is calibrated to this.
 *
 * The model space is SQUARE — 100×100 units, ~33 yards each way — but the field is rendered at
 * `aspect-ratio: 14/10` (desktop) and `10/12` (mobile). Lateral distance is therefore drawn on a
 * different scale from depth: a 40° break in here draws as ~50° on desktop, and lateral motion
 * appears ~1.4× faster than vertical motion at the same speed.
 *
 * Everything in this engine works in model space, where that distortion does not exist. It only
 * matters when judging behavior by eye — see `.kiro/steering/reaction-engine.md`, "The model is
 * square; the rendered field is not". Tune against the harness, not the picture.
 */
export const UNITS_PER_YARD = 3;

/**
 * The y coordinate a receiver reaches `yards` downfield of where he lined up.
 *
 * Depth landmarks belong in YARDS, not units, because a coach says "ten yards" and means it
 * relative to the receiver — but #2 receivers align ~5 units off the ball, so the same landmark
 * lands at a different y for them. That offset used to be applied by hand in five separate
 * params (`readDepthY: 55` for #1, `60` for #2, `verticalDepthY: 60`, ...), which is exactly the
 * sort of derivation that silently breaks the moment a formation moves an alignment.
 */
export function depthLandmarkY(alignmentY: number, yards: number): number {
  return alignmentY - yards * UNITS_PER_YARD;
}

/**
 * Everything observable about a key at one tick. Derived purely from positions over time —
 * never from the route's identity, per the engine's core principle.
 */
export interface KeyObservables {
  /** Milliseconds since the snap for this sample. */
  t: number;
  /** The key's position at this sample. */
  position: Position;
  /** Velocity in field units per second. `vy > 0` means moving BACK toward the LOS. */
  velocity: Position;
  /** Speed (magnitude of velocity) in units per second. */
  speed: number;
  /** Heading in radians (`atan2(vy, vx)`). */
  heading: number;
  /** Highest speed observed up to and including this sample. */
  peakSpeed: number;
  /** The key's initial vertical stem heading, once he has made a real move. */
  stemHeading: number | null;
  /** Smallest angle (radians) between the current heading and the stem. 0 before a stem exists. */
  headingOffStem: number;
  /** Straight-line distance from where the key started. */
  distanceFromStart: number;
  /** Absolute lateral displacement from where the key started. */
  lateralFromStart: number;
  /** Whether the key is moving at all this tick (above {@link MOVEMENT_EPS}). */
  isMoving: boolean;
  /** Where this key lined up at the snap. Depth landmarks are measured from here. */
  alignment: Position;
  /** How many yards downfield of his own alignment he currently is. */
  depthYards: number;
}

/** Smallest angular difference between two headings, in radians (0..PI). */
export function headingDelta(a: number, b: number): number {
  let d = Math.abs(a - b) % (2 * Math.PI);
  if (d > Math.PI) d = 2 * Math.PI - d;
  return d;
}

/**
 * Derive the full observable stream for one key's feed.
 *
 * Index `i` of the result corresponds to index `i` of the feed. Index 0 is the snap frame and
 * carries zero velocity (there is no previous sample to difference against).
 */
export function buildObservableStream(feed: Observation[]): KeyObservables[] {
  const stream: KeyObservables[] = [];
  if (feed.length === 0) return stream;

  const start = feed[0].position;
  let peakSpeed = 0;
  let stemHeading: number | null = null;

  for (let i = 0; i < feed.length; i++) {
    const cur = feed[i].position;
    const prev = i > 0 ? feed[i - 1].position : cur;
    const dtSec = i > 0 ? Math.max((feed[i].t - feed[i - 1].t) / 1000, 1e-6) : 1;

    const vx = (cur.x - prev.x) / dtSec;
    const vy = (cur.y - prev.y) / dtSec;
    const speed = Math.hypot(vx, vy);
    if (speed > peakSpeed) peakSpeed = speed;

    const heading = Math.atan2(vy, vx);
    const isMoving = speed > MOVEMENT_EPS;
    // The stem is the heading of his first real movement — the reference every later break is
    // measured against.
    if (stemHeading === null && isMoving) stemHeading = heading;

    stream.push({
      t: feed[i].t,
      position: { ...cur },
      velocity: { x: vx, y: vy },
      speed,
      heading,
      peakSpeed,
      stemHeading,
      headingOffStem: stemHeading === null ? 0 : headingDelta(heading, stemHeading),
      distanceFromStart: Math.hypot(cur.x - start.x, cur.y - start.y),
      lateralFromStart: Math.abs(cur.x - start.x),
      isMoving,
      alignment: { ...start },
      depthYards: (start.y - cur.y) / UNITS_PER_YARD,
    });
  }

  return stream;
}

/**
 * What the defender is actually reacting to at tick `i` — the observation from
 * `reactionLatencyMs` ago, clamped to the snap.
 *
 * Every read should go through this rather than indexing the stream directly, so a defender
 * can never act on information he has not had time to process.
 */
export function perceive(
  stream: KeyObservables[],
  i: number,
  dtMs: number,
  params: KinematicsParams
): KeyObservables {
  const idx = Math.min(perceivedIndex(i, dtMs, params), stream.length - 1);
  return stream[Math.max(0, idx)];
}

// ============================================
// Threshold predicates
// ============================================
// Kept separate from the stream so the stream stays param-free and shareable. Each policy
// applies its own thresholds to the same observed motion.

/** A hard cut: heading has swung off the vertical stem by more than `breakAngleDeg`. */
export function isHardBreak(obs: KeyObservables, breakAngleDeg: number): boolean {
  const breakAngleRad = (breakAngleDeg * Math.PI) / 180;
  return obs.stemHeading !== null && obs.isMoving && obs.headingOffStem > breakAngleRad;
}

/** A stop/settle: speed has dropped below `settleSpeedFraction` of the key's own peak. */
export function isSettled(obs: KeyObservables, settleSpeedFraction: number): boolean {
  return obs.peakSpeed > MOVEMENT_EPS && obs.speed < settleSpeedFraction * obs.peakSpeed;
}

/** Either kind of break — a cut or a settle. The usual "did something happen" signal. */
export function isBreak(obs: KeyObservables, breakAngleDeg: number, settleSpeedFraction: number): boolean {
  return isHardBreak(obs, breakAngleDeg) || isSettled(obs, settleSpeedFraction);
}

/** Working back toward the QB/LOS faster than `backToLosSpeed` — a hitch or comeback. */
export function isComingBackToLos(obs: KeyObservables, backToLosSpeed: number): boolean {
  return obs.velocity.y > backToLosSpeed;
}

/**
 * Breaking laterally toward a given side. `outSignX` is −1 when that side's sideline is toward
 * decreasing x and +1 when it is toward increasing x, so this distinguishes an out-break from
 * an in-break without knowing the route.
 */
export function isBreakingToward(obs: KeyObservables, outSignX: number, lateralEps: number): boolean {
  return Math.sign(obs.velocity.x) === outSignX && Math.abs(obs.velocity.x) > lateralEps;
}

/**
 * Where this receiver will be in `seconds`, projecting his CURRENT observed velocity forward.
 *
 * This is pursuit, not prophecy. A defender chasing a crosser to where he presently is can never
 * catch him — both are running near the same top speed, so the gap only closes when the receiver
 * stops. Real defenders take an angle at where the man is HEADED, which is exactly this: an
 * extrapolation of motion already observed, with no knowledge of the route.
 *
 * Note the input is already latency-delayed, so leading does not hand back the reaction time the
 * engine deliberately charges — he is projecting forward from a stale picture.
 */
export function projectedPosition(obs: KeyObservables, seconds: number): Position {
  return {
    x: obs.position.x + obs.velocity.x * seconds,
    y: obs.position.y + obs.velocity.y * seconds,
  };
}

/** Has the key reached this depth? Lower y is deeper downfield. */
export function hasReachedDepth(obs: KeyObservables, depthY: number): boolean {
  return obs.position.y <= depthY;
}

/**
 * The y coordinate of a depth landmark for THIS key, given in yards off his own alignment.
 * Use this instead of a hard-coded y so the same rule works for an on-ball receiver and one
 * aligned off the ball, in any formation.
 */
export function landmarkFor(obs: KeyObservables, yards: number): number {
  return depthLandmarkY(obs.alignment.y, yards);
}

/**
 * Is the key attacking vertically *again* — the second half of a double move?
 *
 * This is what separates a hitch-and-go from a slant. Both break off the stem and both end up
 * downfield, but a slant keeps running its new diagonal heading, while a double move re-squares
 * and re-accelerates up the field. Three observable conditions, all required:
 *
 *  1. **Working downfield** faster than `minVerticalSpeed` — not drifting, not settling.
 *  2. **Predominantly vertical** — `|vx|` is small relative to `|vy|`. A slant fails here; its
 *     lateral speed exceeds its vertical speed, which is precisely what makes it a slant.
 *  3. **Back near peak speed** — he has re-accelerated out of the break rather than coasting.
 *
 * Used for pass-off re-entry: a defender who let his key go is not done watching him.
 */
export function isVerticalPush(
  obs: KeyObservables,
  minVerticalSpeed: number,
  maxLateralRatio: number,
  minSpeedFraction: number
): boolean {
  const vy = obs.velocity.y;
  const vx = Math.abs(obs.velocity.x);
  if (vy > -minVerticalSpeed) return false;                     // not driving downfield
  if (vx > Math.abs(vy) * maxLateralRatio) return false;        // diagonal, not vertical
  return obs.peakSpeed > MOVEMENT_EPS && obs.speed >= minSpeedFraction * obs.peakSpeed;
}
